import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Search, Plus, Edit, Trash2, Box, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, Check, PauseCircle, Package, Barcode, Activity, Tag, MapPin, User, ArrowRightLeft, Zap, ThumbsUp } from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { InventaireModalPortal, InventaireViewModalPortal } from './components/inventaireModalPortal';
import FacturationFormModalTabs from '../facturation/components/FacturationFormModalTabs';
import {
  sliceQawaemStats,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  nextInventaireEntityRef,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  fetchInventaireWarehouses,
  buildInventaireWarehouseSelectOptions,
  buildInventaireProductSelectOptions,
  mapInventaireWarehouseSelection,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { useTranslation } from 'react-i18next';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';

const ReapprovisionnementPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.reapprovisionnementPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const { settings } = useSettings();
  const inventoryConfig = useInventoryConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  useArticlesConfig();
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString(dateLocale) : null);
  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Status dropdown
      const isClickOnStatusButton = event.target.closest('[data-replenish-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-replenish-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [replenishments, setReplenishments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Collapsible sections state
  const [replenishFormTab, setReplenishFormTab] = useState('basicInfo');

  const replenishFormTabs = useMemo(() => ([
    { id: 'basicInfo', label: p('section_identification'), icon: TrendingUp, iconColor: 'text-emerald-400' },
    { id: 'quantityInfo', label: p('section_product_quantities'), icon: Package, iconColor: 'text-yellow-400' },
  ]), [p]);

  
  const [formData, setFormData] = useState({ 
    replenishment_number: '', 
    product_id: '', 
    product_name: '',
    product_code: '',
    current_stock: '',
    reorder_point: '',
    reorder_quantity: '',
    requested_quantity: '',
    approved_quantity: '',
    source_location_id: '',
    destination_location_id: '',
    warehouse_id: '',
    priority: 'normal',
    trigger_type: 'manual',
    request_date: new Date().toISOString().split('T')[0],
    expected_date: null,
    assigned_to: '',
    status: 'pending',
    auto_approve: false,
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_replenishments');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Dropdown data states
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  // Multi-column sorting state

  useEffect(() => { 
    loadReplenishments(); 
    loadProducts();
    loadWarehouses();
    loadLocations();
    loadEmployees();
  }, []);
  
  // Auto-check for low stock when reorder is enabled in config
  useEffect(() => {
    if (!inventoryConfig.enableReorder) {
      return;
    }
    if (!inventoryConfig.autoReorderEnabled) {
      return;
    }
    handleAutoCheck({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryConfig.enableReorder, inventoryConfig.autoReorderEnabled]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadReplenishments = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/replenishments');
      setReplenishments(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading replenishments:', error);
      setReplenishments([]);
    }
  };

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      setAvailableProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      setAvailableWarehouses(await fetchInventaireWarehouses());
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setAvailableWarehouses([]);
    } finally {
      setLoadingWarehouses(false);
    }
  };
  
  const loadLocations = async (warehouseId = null) => {
    try {
      setLoadingLocations(true);
      const endpoint = warehouseId 
        ? `/inventory/locations?warehouse_id=${warehouseId}&status=available`
        : '/inventory/locations?status=available';
      const response = await localApiAdapter.get(endpoint);
      setAvailableLocations(response?.data || response || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setAvailableLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };
  
  const loadEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await localApiAdapter.get('/hr/employees');
      setAvailableEmployees(response?.data || response || []);
    } catch (error) {
      console.error('Error loading employees:', error);
      setAvailableEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };
  
  // Auto-check for low stock and create replenishment requests
  const handleAutoCheck = async (options = {}) => {
    const { silent = false } = options;
    try {
      const response = await localApiAdapter.post('/inventory/replenishments/auto-check');
      const data = response?.data ?? response;

      if (data?.success !== false) {
        if (!silent && data?.message) {
          alert(`✅ ${data.message}`);
        }
        loadReplenishments();
      }
    } catch (error) {
      console.error('Error in auto-check:', error);
      if (!silent) {
        alert(`❌ ${p('alert_auto_check_error')}`);
      }
    }
  };
  
  // Approve a replenishment request
  const handleApprove = async (id) => {
    if (!window.confirm(p('approve_confirm'))) {
      return;
    }
    
    try {
      const response = await localApiAdapter.post(`/inventory/replenishments/approve/${id}`);
      const data = response?.data || response;
      
      if (data.success) {
        alert(`✅ ${p('approve_success')}`);
        loadReplenishments();
      }
    } catch (error) {
      console.error('Error approving:', error);
      alert(`❌ ${p('approve_error')}`);
    }
  };

  const generateReplenishmentNumber = useCallback(
    () => nextInventaireEntityRef(settings, 'replenishment', 'REP', replenishments, (r) => r.replenishment_number),
    [settings, replenishments],
  );

  const handleProductSelection = (productId) => {
    if (!productId) {
      setFormData(prev => ({ 
        ...prev, 
        product_id: '', 
        product_name: '', 
        product_code: '',
        current_stock: '',
        reorder_point: '',
        reorder_quantity: ''
      }));
      return;
    }
    const product = availableProducts.find(p => p.id.toString() === productId.toString());
    if (product) {
      const productCode = product.reference || product.code || product.sku || '';
      setFormData(prev => ({ 
        ...prev, 
        product_id: productId,
        product_name: product.name || '',
        product_code: productCode,
        current_stock: product.stock ?? '',
        reorder_point: product.reorder_level ?? product.min_stock ?? inventoryConfig.reorderPoint ?? '',
        reorder_quantity: product.reorder_quantity ?? ''
      }));
    }
  };

  const handleWarehouseSelection = (warehouseId) => {
    if (!warehouseId) {
      setFormData(prev => ({ 
        ...prev, 
        warehouse_id: '', 
        source_location_id: '', 
        destination_location_id: '' 
      }));
      setAvailableLocations([]); // Clear locations
      return;
    }
    // Clear location selections when warehouse changes (locations from old warehouse are invalid)
    setFormData(prev => ({ 
      ...prev, 
      warehouse_id: warehouseId, 
      source_location_id: '', 
      destination_location_id: '' 
    }));
    loadLocations(warehouseId); // Load locations for selected warehouse
  };

  const stats = {
    total: replenishments.length,
    pending: replenishments.filter(r => r.status === 'pending').length,
    approved: replenishments.filter(r => r.status === 'approved').length,
    in_progress: replenishments.filter(r => r.status === 'in_progress').length,
    completed: replenishments.filter(r => r.status === 'completed').length,
    urgent: replenishments.filter(r => r.priority === 'urgent' && r.status !== 'completed').length,
    totalRequested: replenishments.reduce((sum, r) => sum + (parseFloat(r.requested_quantity) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_replenishments', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_priorite', name: p('tab_by_priority'), icon: AlertCircle },
    { id: 'par_trigger', name: p('tab_by_trigger'), icon: Activity }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'pending', name: p('status_pending'), icon: Clock },
    { id: 'approved', name: p('filter_approved'), icon: CheckCircle },
    { id: 'in_progress', name: p('status_in_progress'), icon: Activity }
  ];

  const replenishmentDistribution = {
    all_replenishments: {
      title: p('overview_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { filterKey: 'approved', name: p('dist_approved'), count: stats.approved, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'in_progress', name: p('dist_in_progress'), count: stats.in_progress, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_priorite: {
      title: p('distribution_by_priority'),
      data: [
        { filterKey: 'urgent', name: p('priority_urgent'), count: replenishments.filter(r => r.priority === 'urgent').length, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: 'high', name: p('priority_high'), count: replenishments.filter(r => r.priority === 'high').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'normal', name: p('priority_normal'), count: replenishments.filter(r => r.priority === 'normal').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'low', name: p('priority_low'), count: replenishments.filter(r => r.priority === 'low').length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_trigger: {
      title: p('distribution_by_trigger'),
      data: [
        { filterKey: 'manual', name: p('trigger_manual'), count: replenishments.filter(r => r.trigger_type === 'manual').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'automatic', name: p('trigger_automatic'), count: replenishments.filter(r => r.trigger_type === 'automatic').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterKey: 'reorder_point', name: p('trigger_reorder_point'), count: replenishments.filter(r => r.trigger_type === 'reorder_point').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
      ]
    }
  };

  const distributionInfo = replenishmentDistribution[mainViewMode] || replenishmentDistribution.all_replenishments;

  const baseFilteredReplenishments = replenishments.filter(r => {
    if (!inventaireRecordInDateRange(dateRange, r.request_date, r.expected_date, r.created_at)) return false;

    if (searchTerm && !r.replenishment_number?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !r.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !r.product_code?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_replenishments') {
      if (quickFilter === 'pending' && r.status !== 'pending') return false;
      if (quickFilter === 'approved' && r.status !== 'approved') return false;
      if (quickFilter === 'in_progress' && r.status !== 'in_progress') return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_replenishments':
          if (activeDistributionFilter === 'pending' && r.status !== 'pending') return false;
          if (activeDistributionFilter === 'approved' && r.status !== 'approved') return false;
          if (activeDistributionFilter === 'in_progress' && r.status !== 'in_progress') return false;
          if (activeDistributionFilter === 'completed' && r.status !== 'completed') return false;
          break;
        case 'par_priorite':
          if (activeDistributionFilter === 'urgent' && r.priority !== 'urgent') return false;
          if (activeDistributionFilter === 'high' && r.priority !== 'high') return false;
          if (activeDistributionFilter === 'normal' && r.priority !== 'normal') return false;
          if (activeDistributionFilter === 'low' && r.priority !== 'low') return false;
          break;
        case 'par_trigger':
          if (activeDistributionFilter === 'manual' && r.trigger_type !== 'manual') return false;
          if (activeDistributionFilter === 'automatic' && r.trigger_type !== 'automatic') return false;
          if (activeDistributionFilter === 'reorder_point' && r.trigger_type !== 'reorder_point') return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('replenishment_number'),
    textColumnRule('product_name'),
    { key: 'stock', getValue: (r) => `${r.current_stock ?? ''} ${r.reorder_point ?? ''}`.trim() },
    textColumnRule('requested_quantity', (r) => String(r.requested_quantity ?? '')),
    exactColumnRule('priority', (r) => r.priority),
    exactColumnRule('status', (r) => r.status),
  ], []);

  const filteredReplenishments = useMemo(
    () => applyColumnFilters(baseFilteredReplenishments, colFilters, columnFilterRules),
    [baseFilteredReplenishments, colFilters, columnFilterRules],
  );
  const visibleReplenishments = useMemo(
    () => sliceInventaireListItems(filteredReplenishments, itemsPerPage),
    [filteredReplenishments, itemsPerPage],
  );

  const openNewReplenishmentModal = () => {
    if (!inventoryConfig.enableReorder) {
      window.alert(p('notice_reorder_disabled', { defaultValue: 'إعادة الطلب معطّلة من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setReplenishFormTab('basicInfo');
    setFormData({
      replenishment_number: inventoryConfig.autoGenerateReorderReference !== false ? generateReplenishmentNumber() : '',
      product_id: '',
      product_name: '',
      product_code: '',
      current_stock: '',
      reorder_point: String(inventoryConfig.reorderPoint ?? ''),
      reorder_quantity: '',
      requested_quantity: '',
      approved_quantity: '',
      source_location_id: '',
      destination_location_id: '',
      warehouse_id: '',
      priority: 'normal',
      trigger_type: inventoryConfig.autoReorderEnabled ? 'automatic' : 'manual',
      request_date: null,
      expected_date: null,
      assigned_to: '',
      status: 'pending',
      auto_approve: false,
      notes: '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const replenishmentNumber = (formData.replenishment_number || '').trim() || generateReplenishmentNumber();
      const submitData = { 
        replenishment_number: replenishmentNumber,
        product_id: parseInt(formData.product_id) || null,
        product_name: formData.product_name,
        product_code: formData.product_code,
        current_stock: parseFloat(formData.current_stock) || 0,
        reorder_point: parseFloat(formData.reorder_point) || 0,
        reorder_quantity: parseFloat(formData.reorder_quantity) || 0,
        requested_quantity: parseFloat(formData.requested_quantity) || 0,
        approved_quantity: parseFloat(formData.approved_quantity) || 0,
        source_location_id: parseInt(formData.source_location_id) || null,
        destination_location_id: parseInt(formData.destination_location_id) || null,
        warehouse_id: parseInt(formData.warehouse_id) || null,
        priority: formData.priority,
        trigger_type: formData.trigger_type,
        request_date: formData.request_date ? new Date(formData.request_date).toISOString() : null,
        expected_date: formData.expected_date ? new Date(formData.expected_date).toISOString() : null,
        assigned_to: parseInt(formData.assigned_to) || null,
        status: formData.status,
        auto_approve: formData.auto_approve || false,
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/inventory/replenishments/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'إعادة التموين' }));
      } else {
        await localApiAdapter.post('/inventory/replenishments', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'إعادة التموين' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      return;
    } finally {
      resetForm();
      loadReplenishments();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      replenishment_number: record.replenishment_number || '',
      product_id: record.product_id?.toString() || '',
      product_name: record.product_name || '',
      product_code: record.product_code || '',
      current_stock: record.current_stock?.toString() || '',
      reorder_point: record.reorder_point?.toString() || '',
      reorder_quantity: record.reorder_quantity?.toString() || '',
      requested_quantity: record.requested_quantity?.toString() || '',
      approved_quantity: record.approved_quantity?.toString() || '',
      source_location_id: record.source_location_id?.toString() || '',
      destination_location_id: record.destination_location_id?.toString() || '',
      warehouse_id: record.warehouse_id?.toString() || '',
      priority: record.priority || 'normal',
      trigger_type: record.trigger_type || 'manual',
      request_date: record.request_date ? new Date(record.request_date) : null,
      expected_date: record.expected_date ? new Date(record.expected_date) : null,
      assigned_to: record.assigned_to?.toString() || '',
      status: record.status || 'pending',
      auto_approve: record.auto_approve || false,
      notes: record.notes || ''
    });
    setReplenishFormTab('basicInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/inventory/replenishments/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'إعادة التموين' }));
        loadReplenishments();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const filteredReplenishmentsIds = useMemo(() => filteredReplenishments.map((r) => r.id), [filteredReplenishments]);
  const allRowsSelected = filteredReplenishmentsIds.length > 0 && filteredReplenishmentsIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredReplenishmentsIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };
  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/inventory/replenishments/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'إعادة التموين' }));
        setSelectedRecords([]);
        loadReplenishments();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      replenishment_number: '', 
      product_id: '', 
      product_name: '',
      product_code: '',
      current_stock: '',
      reorder_point: '',
      reorder_quantity: '',
      requested_quantity: '',
      approved_quantity: '',
      source_location_id: '',
      destination_location_id: '',
      warehouse_id: '',
      priority: 'normal',
      trigger_type: 'manual',
      request_date: null,
      expected_date: null,
      assigned_to: '',
      status: 'pending',
      auto_approve: false,
      notes: ''
    });
    setEditingRecord(null);
    setReplenishFormTab('basicInfo');
    setShowModal(false);
  };


  // Export handlers
  const handleExport = () => {
    exportToCSV(filteredReplenishments, 'reapprovisionnements', [
      { key: 'replenishment_number', label: p('export_col_number') },
      { key: 'product_name', label: p('export_col_product') },
      { key: 'requested_quantity', label: p('export_col_requested') },
      { key: 'priority', label: p('col_priority') },
      { key: 'status', label: p('col_status') }
    ]);
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const filteredData = filteredReplenishments;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('export_col_number')}</th>
                <th>${p('export_col_product')}</th>
                <th>${p('export_col_current_stock')}</th>
                <th>${p('export_col_requested')}</th>
                <th>${p('col_priority')}</th>
                <th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.replenishment_number || p('na')}</td>
                  <td>${item.product_name || p('na')}</td>
                  <td>${item.current_stock || 0}</td>
                  <td>${item.requested_quantity || 0}</td>
                  <td>${item.priority || p('na')}</td>
                  <td>${item.status || p('na')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_title'), {});
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      
      const filteredData = filteredReplenishments;
      
      const columns = [
        { key: 'replenishment_number', label: p('export_col_number'), width: 20 },
        { key: 'product_name', label: p('export_col_product'), width: 25 },
        { key: 'product_code', label: p('export_col_product_code'), width: 15 },
        { key: 'current_stock', label: p('export_col_current_stock'), width: 12 },
        { key: 'reorder_point', label: p('export_col_reorder_point'), width: 15 },
        { key: 'requested_quantity', label: p('export_col_requested'), width: 15 },
        { key: 'approved_quantity', label: p('export_col_approved_qty'), width: 15 },
        { key: 'priority', label: p('col_priority'), width: 12 },
        { key: 'trigger_type', label: p('export_col_trigger'), width: 15 },
        { key: 'status', label: p('col_status'), width: 12 }
      ];
      
      const data = filteredData.map(item => ({
        replenishment_number: item.replenishment_number || '',
        product_name: item.product_name || '',
        product_code: item.product_code || '',
        current_stock: item.current_stock || 0,
        reorder_point: item.reorder_point || 0,
        requested_quantity: item.requested_quantity || 0,
        approved_quantity: item.approved_quantity || 0,
        priority: item.priority || '',
        trigger_type: item.trigger_type || '',
        status: item.status || ''
      }));
      
      await generateReportExcel('replenishment_report', p('report_title'), {
        sheetName: p('report_sheet_name'),
      });
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const statusOptions = [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'approved', label: p('status_approved'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'in_progress', label: p('status_in_progress'), icon: Activity, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'completed', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/inventory/replenishments/${recordId}`, { status: newStatus });
      await loadReplenishments();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };


  const getPriorityBadge = (priority) => {
    const priorities = {
      urgent: { label: p('priority_urgent'), color: 'bg-red-500/20 text-red-400 border-red-500/30' },
      high: { label: p('priority_high'), color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      normal: { label: p('priority_normal'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      low: { label: p('priority_low'), color: 'bg-green-500/20 text-green-400 border-green-500/30' }
    };
    const config = priorities[priority] || priorities.normal;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>{config.label}</span>;
  };

  const getStockLevel = (current, reorder) => {
    const currentStock = parseFloat(current) || 0;
    const reorderPoint = parseFloat(reorder) || 0;
    
    // Don't show badge if no reorder point is set
    if (reorderPoint === 0) {
      return null;
    }
    
    if (currentStock === 0) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">{p('stock_out')}</span>;
    } else if (currentStock <= reorderPoint) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30">{p('stock_critical')}</span>;
    } else {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">{p('stock_normal')}</span>;
    }
  };

  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'replenishment_number', labelKey: 'col_replenishment_number' },
    { key: 'product_name', labelKey: 'col_product' },
    { key: 'stock', labelKey: 'col_stock' },
    { key: 'requested_quantity', labelKey: 'col_requested_qty' },
    { key: 'priority', labelKey: 'col_priority' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: TrendingUp },
    { key: 'stat_pending', label: p('stat_pending'), value: stats.pending, icon: Clock },
    { key: 'stat_ordered', label: p('stat_ordered'), value: stats.ordered, icon: Package },
    { key: 'stat_received', label: p('stat_received'), value: stats.received, icon: CheckCircle },
  ], 4), [stats, p]);


  const handleImport = () => {
    triggerFileInput((file) => {
      importFromFile(file, () => loadReplenishments());
    });
  };

  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExport,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enableReorder ? p('notice_reorder_disabled_banner', { defaultValue: 'إعادة الطلب معطّلة — فعّلها من إعدادات المخزون ← إعادة الطلب.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={TrendingUp}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      onCreate={openNewReplenishmentModal}
      createLabel={p('new_reorder')}
      importExport={importExportProps}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
    >
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  
                  {isColVisible('replenishment_number') && (

                  
                    <TableColumnHeaderFilter columnKey="replenishment_number" label={p('col_replenishment_number')} value={colFilters.replenishment_number ?? ''} onChange={(v) => setColFilter('replenishment_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('product_name') && (

                    <TableColumnHeaderFilter columnKey="product_name" label={p('col_product')} value={colFilters.product_name ?? ''} onChange={(v) => setColFilter('product_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('stock') && (

                    <TableColumnHeaderFilter columnKey="stock" label={p('col_stock')} value={colFilters.stock ?? ''} onChange={(v) => setColFilter('stock', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('requested_quantity') && (

                    <TableColumnHeaderFilter columnKey="requested_quantity" label={p('col_requested_qty')} value={colFilters.requested_quantity ?? ''} onChange={(v) => setColFilter('requested_quantity', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('priority') && (

                    <TableColumnHeaderFilter columnKey="priority" label={p('col_priority')} value={colFilters.priority ?? ''} onChange={(v) => setColFilter('priority', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={INVENTAIRE_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visibleReplenishments.length > 0 ? (
                  visibleReplenishments.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      
                      {isColVisible('replenishment_number') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-white font-semibold flex items-center gap-2">
                            <TrendingUp size={16} className="text-blue-400" />
                            {record.replenishment_number}
                          </span>
                          {record.trigger_type === 'automatic' ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1 w-fit">
                              <Zap size={12} />
                              {p('trigger_automatic_option')}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1 w-fit">
                              <User size={12} />
                              {p('trigger_manual_option')}
                            </span>
                          )}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('product_name') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.product_name || p('na')}</span>
                          {record.product_code && <span className="text-white/50 text-xs mt-0.5">{p('label_code')}: {record.product_code}</span>}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('stock') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-white/70 text-sm">
                            {p('stock_current')}: {record.current_stock > 0 ? record.current_stock : (record.current_stock === 0 ? '0' : '-')}
                          </span>
                          <span className="text-white/70 text-sm">
                            {p('stock_point')}: {record.reorder_point > 0 ? record.reorder_point : '-'}
                          </span>
                          {getStockLevel(record.current_stock, record.reorder_point)}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('requested_quantity') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">
                            {record.requested_quantity > 0 ? record.requested_quantity : '-'}
                          </span>
                          {record.approved_quantity > 0 && (
                            <span className="text-white/50 text-xs mt-0.5">{p('qty_approved_short')}: {record.approved_quantity}</span>
                          )}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('priority') && (

                      
                        <td className="px-6 py-4">{getPriorityBadge(record.priority)}</td>

                      
                      )}
                      
                      {isColVisible('status') && (

                      
                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div></td>

                      
                      )}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-1">
                          {record.trigger_type === 'automatic' && record.status === 'pending' && (
                            <button 
                              onClick={() => handleApprove(record.id)} 
                              className="p-1.5 text-amber-400 hover:text-amber-300 transition-colors" 
                              title={p('approve_auto_title')}
                            >
                              <ThumbsUp size={16} />
                            </button>
                          )}
                          <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><TrendingUp size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleReplenishments.length > 0 ? (
              visibleReplenishments.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <TrendingUp size={20} className="text-blue-400" />
                          {record.replenishment_number}
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                        {getPriorityBadge(record.priority)}
                        {getStockLevel(record.current_stock, record.reorder_point)}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_product')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}:</span> {record.product_name || p('na')}</div>
                            <div><span className="font-medium">{p('label_code')}:</span> {record.product_code || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_stock')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('stock_current')}:</span> {record.current_stock || 0}</div>
                            <div><span className="font-medium">{p('label_reorder_point')}:</span> {record.reorder_point || 0}</div>
                            <div><span className="font-medium">{p('label_reorder_qty')}:</span> {record.reorder_quantity || 0}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_quantities')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_requested')}:</span> {record.requested_quantity || 0}</div>
                            <div><span className="font-medium">{p('label_approved')}:</span> {record.approved_quantity || 0}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.request_date && <div><span className="font-medium">{p('label_request_date')}:</span> {formatDate(record.request_date)}</div>}
                            {record.expected_date && <div><span className="font-medium">{p('label_expected_date')}:</span> {formatDate(record.expected_date)}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p>{record.notes || p('no_notes')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <TrendingUp size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      {/* View Modal - Shortened for space, continuing in edit modal section */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp size={18} className="text-blue-400" />
                    {p('section_identification')}
                  </h3>
                  <div className={INVENTAIRE_FORM_GRID}>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_replenishment_number')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.replenishment_number}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_priority')}</p>
                      <div className="flex">{getPriorityBadge(viewingRecord.priority)}</div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_trigger')}</p>
                      <p className="text-white font-medium">{viewingRecord.trigger_type || p('na')}</p>
                    </div>
                  </div>
                </div>

                <div className={INVENTAIRE_FORM_GRID}>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Package size={18} className="text-green-400" />
                      {p('section_product_stock')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_product')}</p>
                        <p className="text-white font-medium">{viewingRecord.product_name || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_current_stock')}</p>
                        <p className="text-white font-medium">{viewingRecord.current_stock || 0}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_stock_level')}</p>
                        <div className="flex">{getStockLevel(viewingRecord.current_stock, viewingRecord.reorder_point)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Gauge size={18} className="text-purple-400" />
                      {p('section_quantities_view')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_requested_qty')}</p>
                        <p className="text-white font-medium">{viewingRecord.requested_quantity || 0}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_approved_qty')}</p>
                        <p className="text-white font-medium">{viewingRecord.approved_quantity || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-yellow-400" />
                    {p('section_notes')}
                  </h3>
                  <p className="text-white/80">{viewingRecord.notes || p('no_notes')}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Edit/Create Modal - Continuing with form sections */}
      {showModal && (
        <InventaireModalPortal show={showModal} onClose={resetForm} maxWidth="1200px">
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('edit_replenishment') : p('new_replenishment_modal')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="replenish-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <Check size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            
            <FacturationFormModalTabs
              tabs={replenishFormTabs}
              activeTab={replenishFormTab}
              onTabChange={setReplenishFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('edit_replenishment') : p('new_replenishment_modal')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="replenish-form" onSubmit={handleSubmit}>
              {replenishFormTab === 'basicInfo' && (
              <div className={INVENTAIRE_FORM_GRID}>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_replenishment_number', { defaultValue: 'رمز إعادة التزويد' })}</label>
                            <input
                              type="text"
                              required
                              value={formData.replenishment_number || ''}
                              onChange={(e) => setFormData({ ...formData, replenishment_number: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                              placeholder={p('placeholder_replenishment_number', { defaultValue: 'REP-001' })}
                              dir="ltr"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_priority')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.priority}
                              onChange={(value) => setFormData({ ...formData, priority: value })}
                              options={[
                                { value: 'urgent', label: p('priority_urgent') },
                                { value: 'high', label: p('priority_high') },
                                { value: 'normal', label: p('priority_normal') },
                                { value: 'low', label: p('priority_low') }
                              ]}
                              placeholder={p('select_priority')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={16} /> }))}
                              placeholder={p('select_status')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_trigger_type')}</label>
                            <CustomSelect
                              value={formData.trigger_type}
                              onChange={(value) => setFormData({ ...formData, trigger_type: value })}
                              options={[
                                { value: 'manual', label: p('trigger_manual_option') },
                                { value: 'automatic', label: p('trigger_automatic_option') },
                                { value: 'reorder_point', label: p('trigger_reorder_option') }
                              ]}
                              placeholder={p('trigger_type_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('request_date')}</label>
                            <CustomDatePicker
                              value={formData.request_date}
                              onChange={(date) => setFormData({ ...formData, request_date: date })}
                              placeholder={p('request_date_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('expected_date')}</label>
                            <CustomDatePicker
                              value={formData.expected_date}
                              onChange={(date) => setFormData({ ...formData, expected_date: date })}
                              placeholder={p('expected_date_placeholder')}
                              minDate={formData.request_date}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                              <span className="text-red-400">*</span> {p('warehouse_select_first')}
                            </label>
                            {loadingWarehouses ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.warehouse_id}
                                onChange={(value) => handleWarehouseSelection(value)}
                                options={availableWarehouses.map(warehouse => ({
                                  value: warehouse.id,
                                  label: `${warehouse.name}${warehouse.code ? ' - ' + warehouse.code : ''}`
                                }))}
                                placeholder={p('select_warehouse_first')}
                                searchable
                              />
                            )}
                            {!formData.warehouse_id && (
                              <p className="text-yellow-400/80 text-xs mt-1 flex items-center gap-1">
                                <AlertTriangle size={12} />
                                {p('warehouse_hint')}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('source_location')}</label>
                            {!formData.warehouse_id ? (
                              <div className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/30 cursor-not-allowed">
                                {p('select_warehouse_first')}
                              </div>
                            ) : loadingLocations ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.source_location_id}
                                onChange={(value) => setFormData({ ...formData, source_location_id: value })}
                                options={availableLocations.map(location => ({
                                  value: location.id.toString(),
                                  label: `${location.location_code} - ${location.location_name || ''}`
                                }))}
                                placeholder={p('select_source_location')}
                                searchable
                              />
                            )}
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('destination_location')}</label>
                            {!formData.warehouse_id ? (
                              <div className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/30 cursor-not-allowed">
                                {p('select_warehouse_first')}
                              </div>
                            ) : loadingLocations ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.destination_location_id}
                                onChange={(value) => setFormData({ ...formData, destination_location_id: value })}
                                options={availableLocations.map(location => ({
                                  value: location.id.toString(),
                                  label: `${location.location_code} - ${location.location_name || ''}`
                                }))}
                                placeholder={p('select_destination_location')}
                                searchable
                              />
                            )}
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('assigned_to')}</label>
                            {loadingEmployees ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.assigned_to}
                                onChange={(value) => setFormData({ ...formData, assigned_to: value })}
                                options={availableEmployees.map(employee => ({
                                  value: employee.id.toString(),
                                  label: `${employee.first_name} ${employee.last_name}${employee.position ? ' - ' + employee.position : ''}`
                                }))}
                                placeholder={p('select_employee')}
                                searchable
                              />
                            )}
                          </div></div>
              )}

              {replenishFormTab === 'quantityInfo' && (
              <div className={INVENTAIRE_FORM_GRID}>{/* Product Selection */}
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_product_required')} <span className="qawaem-required-mark">*</span></label>
                            {loadingProducts ? (
                              <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                            ) : (
                              <CustomSelect
                                value={formData.product_id}
                                onChange={(value) => handleProductSelection(value)}
                                options={availableProducts.map(product => ({
                                  value: product.id,
                                  label: `${product.name} - ${product.reference || product.code || product.sku || ''} - ${p('product_stock_label')}: ${product.stock || 0}`
                                }))}
                                placeholder={p('select_product')}
                                searchable
                              />
                            )}
                          </div>
                          
                          {/* Quantities Grid */}
                          <div className={INVENTAIRE_FORM_GRID}>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('current_stock')}</label>
                            <CustomNumberInput
                              value={formData.current_stock}
                              onChange={(value) => setFormData({ ...formData, current_stock: value })}
                              placeholder="0"
                              step={1}
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('reorder_point')}</label>
                            <CustomNumberInput
                              value={formData.reorder_point}
                              onChange={(value) => setFormData({ ...formData, reorder_point: value })}
                              placeholder="0"
                              step={1}
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('reorder_quantity')}</label>
                            <CustomNumberInput
                              value={formData.reorder_quantity}
                              onChange={(value) => setFormData({ ...formData, reorder_quantity: value })}
                              placeholder="0"
                              step={1}
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('requested_quantity')} <span className="qawaem-required-mark">*</span></label>
                            <CustomNumberInput
                              value={formData.requested_quantity}
                              onChange={(value) => setFormData({ ...formData, requested_quantity: value })}
                              placeholder="0"
                              step={1}
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('approved_quantity')}</label>
                            <CustomNumberInput
                              value={formData.approved_quantity}
                              onChange={(value) => setFormData({ ...formData, approved_quantity: value })}
                              placeholder="0"
                              step={1}
                              min={0}
                            />
                          </div>
                          <div className="flex items-center">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.auto_approve}
                                onChange={(e) => setFormData({ ...formData, auto_approve: e.target.checked })}
                                className="w-5 h-5 text-blue-600 bg-white/10 border-white/20 rounded focus:ring-blue-500 focus:ring-2"
                              />
                              <span className="ml-3 text-white font-medium">{p('auto_approve')}</span>
                            </label>
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('notes_placeholder')} />
                          </div>
                          </div></div>
              )}
              </form>
            </div>
        </InventaireModalPortal>
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default ReapprovisionnementPage;

